import { memo, useCallback, useRef, useState } from 'react';
import { AlertBanner, Badge, Button, IconHighlight, ModalLayout } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Trash2 } from 'lucide-react';

import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';
import { NO_HEADER_LINE, WIDE_MODAL } from '../shared/modalClasses';

const TITLE_ID = 'remove-resource-access-title';

/**
 * Confirm before removing a member's direct access to a resource: the resource with its type, the
 * access, source and date, and what is left afterwards (a red notice when the member loses every
 * path, a warning when another path keeps the access).
 *
 * @param {object} props
 * @param {object|null} props.resource UI resource to remove, or null when closed.
 * @param {string} props.memberName
 * @param {string} props.dateLabel Formatted grant date.
 * @param {string} props.typeLabel Translated type.
 * @param {string} props.sourceLabel Translated source.
 * @param {() => void} props.onClose
 * @param {(resource: object) => Promise<boolean>} props.onConfirm Resolves `true` when removed (it closes).
 */
function RemoveResourceAccessBody({ resource, memberName, dateLabel, typeLabel, sourceLabel, onClose, onConfirm }) {
  const { t } = useTranslation('people-access');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const handleClose = useCallback(() => {
    if (!submittingRef.current) onClose();
  }, [onClose]);
  useDialogKeyboard(true, handleClose, TITLE_ID);

  const handleConfirm = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    let ok = false;
    try {
      ok = await onConfirm(resource);
    } catch {
      // `onConfirm` resolves false on failure; a throw counts the same.
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
    if (ok) onClose();
  }, [onConfirm, onClose, resource]);

  const hasOtherPath = resource.otherPaths > 0;

  return (
    <ModalLayout
      isOpen
      onClose={handleClose}
      titleId={TITLE_ID}
      closeAriaLabel={t('common.close')}
      closeDisabled={submitting}
      useBodyPortal
      titlePrefix={<IconHighlight icon={Trash2} bgColor="Error" iconColor="Error" size="medium" />}
      title={t('memberTabs.resources.removeModal.title')}
      subtitle={t('memberTabs.resources.removeModal.subtitle', { name: memberName })}
      panelClassName={NO_HEADER_LINE}
      maxWidthClassName={WIDE_MODAL}
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-content">{resource.name}</span>
          <Badge variant="neutral">{typeLabel}</Badge>
        </div>
        <dl className="grid grid-cols-3 gap-4 text-sm">
          {[
            [t('memberTabs.resources.removeModal.access'), resource.access],
            [t('memberTabs.resources.removeModal.source'), sourceLabel],
            [t('memberTabs.resources.removeModal.granted'), dateLabel],
          ].map(([label, value]) => (
            <div key={label} className="flex flex-col gap-0.5">
              <dt className="text-xs text-content-muted">{label}</dt>
              <dd className="text-content-secondary">{value}</dd>
            </div>
          ))}
        </dl>
        {hasOtherPath ? (
          <AlertBanner tone="warning" title={t('memberTabs.resources.removeModal.otherTitle')}>
            {t('memberTabs.resources.removeModal.otherBody', { name: memberName, count: resource.otherPaths })}
          </AlertBanner>
        ) : (
          <AlertBanner tone="danger" title={t('memberTabs.resources.removeModal.noOtherTitle')}>
            {t('memberTabs.resources.removeModal.noOtherBody', { name: memberName })}
          </AlertBanner>
        )}
      </div>
      <div className="flex justify-end gap-2 px-6 pb-6">
        <Button variant="secondary" size="md" data-autofocus disabled={submitting} onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button variant="danger" size="md" loading={submitting} onClick={handleConfirm}>
          {t('memberTabs.resources.removeModal.confirm')}
        </Button>
      </div>
    </ModalLayout>
  );
}

function RemoveResourceAccessModal({ resource, ...props }) {
  return resource ? <RemoveResourceAccessBody resource={resource} {...props} /> : null;
}

export default memo(RemoveResourceAccessModal);
